import type { MeasurementPlan, Metric, MetricCategory } from './schema';

type Lang = 'fr' | 'en';

function t(lang: Lang) {
  return lang === 'en'
    ? {
        title: 'MVP Measurement Plan',
        executiveSummary: 'Executive summary',
        businessObjective: 'Business objective',
        northStarMetric: 'North Star metric',
        baseline: 'Baseline',
        assumptions: 'Assumptions',
        scope: 'Scope',
        nonScope: 'Out of scope',
        metrics: 'Metrics',
        instrumentationPlan: 'Instrumentation plan',
        acceptanceCriteria: 'Acceptance criteria',
        technicalDebt: 'Technical debt',
        businessDebt: 'Business debt',
        aiEvaluation: 'AI evaluation',
        continueCorrectPivotStop: 'Continue / Correct / Pivot / Stop rules',
        handoverCriteria: 'Handover criteria',
        missingInformation: 'Missing information',
        category: 'Category',
        reason: 'Reason',
        formula: 'Formula',
        unit: 'Unit',
        baselineLabel: 'Baseline',
        target: 'Target',
        dataSource: 'Data source',
        cadence: 'Cadence',
        owner: 'Owner',
        decisionThreshold: 'Decision threshold',
        pairedGuardrail: 'Paired guardrail',
        yes: 'Yes',
        no: 'No',
        generatedBy: 'Generated by Averiane',
        unknownPlaceholder: 'To confirm',
        howToUse: 'How to use this plan',
        howToUseItems: [
          'One owner per metric: every metric below names a single accountable owner. Until one is confirmed, the metric is not operational.',
          'Measure the baseline before chasing the target: a target without a measured baseline is a guess, not a goal.',
          'Review each metric at its stated cadence, in a fixed ritual (weekly or monthly review), not on demand.',
          'Apply the decision rules as written: thresholds exist to remove debate at review time.',
          'Track missing information to closure: every "To confirm" below is an action to assign, not a footnote.',
        ],
        metricChecklistTitle: 'Metric action checklists',
        metricChecklistIntro:
          'Each metric becomes operational only when every box below is ticked. Copy this section into your sprint backlog.',
        checklist: {
          sourceConfirmed: 'Data source confirmed and accessible',
          baselineMeasured: 'Baseline measured',
          dashboardSetUp: 'Dashboard / instrumentation set up',
          ownerNamed: 'Accountable owner named',
          reviewPlanned: 'Review slot booked at the stated cadence',
        },
        first30Days: 'First 30 days checklist',
        first30DaysItems: [
          'Week 1 — Validate scope, assumptions and the North Star definition with the team; assign every missing owner and data source.',
          'Week 2 — Instrument the data sources; capture baselines for every metric, even approximate ones.',
          'Weeks 3–4 — First full review cycle against the decision thresholds; log what the metrics disprove, not only what they confirm.',
          'Day 30 — Apply the Continue / Correct / Pivot / Stop rules and record the decision in this document.',
        ],
        automationNote:
          'Want to automate the collection of these metrics with AI? We will walk you through the approach in a 20-minute call: https://www.averiane.com/en/contact',
        pairedSummaryTitle: 'Paired gain / guardrail summary',
      }
    : {
        title: 'Plan de mesure du MVP',
        executiveSummary: 'Résumé exécutif',
        businessObjective: 'Objectif métier',
        northStarMetric: 'Métrique North Star',
        baseline: 'Référence',
        assumptions: 'Hypothèses',
        scope: 'Périmètre',
        nonScope: 'Hors périmètre',
        metrics: 'Métriques',
        instrumentationPlan: "Plan d'instrumentation",
        acceptanceCriteria: "Critères d'acceptation",
        technicalDebt: 'Dette technique',
        businessDebt: 'Dette métier',
        aiEvaluation: "Évaluation de l'IA",
        continueCorrectPivotStop: 'Règles Continuer / Corriger / Pivoter / Arrêter',
        handoverCriteria: 'Critères de transfert',
        missingInformation: 'Informations manquantes',
        category: 'Catégorie',
        reason: 'Raison',
        formula: 'Formule',
        unit: 'Unité',
        baselineLabel: 'Référence',
        target: 'Cible',
        dataSource: 'Source de données',
        cadence: 'Cadence',
        owner: 'Responsable',
        decisionThreshold: 'Seuil de décision',
        pairedGuardrail: 'Garde-fou apparié',
        yes: 'Oui',
        no: 'Non',
        generatedBy: 'Généré par Averiane',
        unknownPlaceholder: 'À confirmer',
        howToUse: 'Mode d’emploi de ce plan',
        howToUseItems: [
          'Un responsable par métrique : chaque métrique ci-dessous désigne un responsable identifié. Tant qu’il n’est pas confirmé, la métrique n’est pas opérationnelle.',
          'Mesurer la référence avant de viser la cible : une cible sans référence mesurée est une estimation, pas un objectif.',
          'Revoir chaque métrique à sa cadence déclarée, dans un rituel fixe (revue hebdomadaire ou mensuelle), pas à la demande.',
          'Appliquer les règles de décision telles quelles : les seuils existent pour supprimer le débat au moment de la revue.',
          'Suivre les informations manquantes jusqu’à leur résolution : chaque « À confirmer » ci-dessous est une action à assigner, pas une note de bas de page.',
        ],
        metricChecklistTitle: 'Checklists d’action par métrique',
        metricChecklistIntro:
          'Une métrique devient opérationnelle uniquement lorsque chaque case ci-dessous est cochée. Copiez cette section dans votre backlog de sprint.',
        checklist: {
          sourceConfirmed: 'Source de données confirmée et accessible',
          baselineMeasured: 'Référence mesurée',
          dashboardSetUp: 'Tableau de bord / instrumentation mis en place',
          ownerNamed: 'Responsable identifié',
          reviewPlanned: 'Créneau de revue réservé à la cadence déclarée',
        },
        first30Days: 'Checklist des 30 premiers jours',
        first30DaysItems: [
          'Semaine 1 — Valider le périmètre, les hypothèses et la définition de la North Star avec l’équipe ; assigner chaque responsable et source de données manquants.',
          'Semaine 2 — Instrumenter les sources de données ; mesurer les références de chaque métrique, même approximatives.',
          'Semaines 3–4 — Premier cycle de revue complet contre les seuils de décision ; consigner ce que les métriques réfutent, pas seulement ce qu’elles confirment.',
          'Jour 30 — Appliquer les règles Continuer / Corriger / Pivoter / Arrêter et consigner la décision dans ce document.',
        ],
        automationNote:
          'Vous souhaitez automatiser la collecte de ces métriques par IA ? Nous vous expliquons l’approche en 20 minutes, par téléphone : https://www.averiane.com/fr/contact',
        pairedSummaryTitle: 'Synthèse des gains et garde-fous appariés',
      };
}

function escapeMarkdown(text: string): string {
  return text.replace(/\|/g, '\\|');
}

function metricRow(metric: Metric): string {
  const cells = [
    escapeMarkdown(metric.name),
    metric.category,
    escapeMarkdown(metric.reason),
    escapeMarkdown(metric.formula),
    escapeMarkdown(metric.unit),
    escapeMarkdown(metric.baseline),
    escapeMarkdown(metric.target),
    escapeMarkdown(metric.dataSource),
    escapeMarkdown(metric.cadence),
    escapeMarkdown(metric.owner),
    escapeMarkdown(metric.decisionThreshold),
    escapeMarkdown(metric.pairedGuardrail),
  ];
  return `| ${cells.join(' | ')} |`;
}

function categoryEmoji(category: MetricCategory): string {
  switch (category) {
    case 'business':
      return '📈';
    case 'quality':
      return '🛡️';
    case 'risk':
      return '⚠️';
    case 'ai':
      return '🤖';
    default:
      return '•';
  }
}

export function buildConfluenceMarkdown(plan: MeasurementPlan, lang: Lang): string {
  const labels = t(lang);
  const now = new Date().toISOString().split('T')[0];

  const metricHeaders = [
    'Name',
    labels.category,
    labels.reason,
    labels.formula,
    labels.unit,
    labels.baselineLabel,
    labels.target,
    labels.dataSource,
    labels.cadence,
    labels.owner,
    labels.decisionThreshold,
    labels.pairedGuardrail,
  ];

  const lines: string[] = [];

  lines.push(`# ${plan.title}`);
  lines.push('');
  lines.push(`> ${labels.generatedBy} — ${now}`);
  lines.push('');
  lines.push(`## ${labels.howToUse}`);
  labels.howToUseItems.forEach((item) => {
    lines.push(`- ${item}`);
  });
  lines.push('');

  lines.push(`## ${labels.executiveSummary}`);
  lines.push(plan.executiveSummary);
  lines.push('');

  lines.push(`## ${labels.businessObjective}`);
  lines.push(plan.businessObjective);
  lines.push('');

  lines.push(`## ${labels.northStarMetric}`);
  lines.push(plan.northStarMetric);
  lines.push('');

  lines.push(`## ${labels.baseline}`);
  lines.push(plan.baseline);
  lines.push('');

  lines.push(`## ${labels.assumptions}`);
  plan.assumptions.forEach((item) => {
    lines.push(`- ${item}`);
  });
  lines.push('');

  lines.push(`## ${labels.scope}`);
  plan.scope.forEach((item) => {
    lines.push(`- ${item}`);
  });
  lines.push('');

  lines.push(`## ${labels.nonScope}`);
  plan.nonScope.forEach((item) => {
    lines.push(`- ${item}`);
  });
  lines.push('');

  lines.push(`## ${labels.metrics}`);
  lines.push('');
  lines.push(`| ${metricHeaders.join(' | ')} |`);
  lines.push(`|${metricHeaders.map(() => '---').join('|')}|`);
  plan.metrics.forEach((metric) => {
    lines.push(metricRow(metric));
  });
  lines.push('');

  // Per-metric action checklists: the operational "what to do / what to measure" value
  lines.push(`## ${labels.metricChecklistTitle}`);
  lines.push('');
  lines.push(`> ${labels.metricChecklistIntro}`);
  lines.push('');
  plan.metrics.forEach((metric) => {
    lines.push(`### ${categoryEmoji(metric.category)} ${escapeMarkdown(metric.name)}`);
    lines.push('');
    lines.push(`- ${isEn(lang) ? 'Category' : labels.category}: ${metric.category}`);
    lines.push(
      `- ${labels.formula}: ${escapeMarkdown(metric.formula)} (${escapeMarkdown(metric.unit)})`
    );
    lines.push(`- ${labels.target}: ${escapeMarkdown(metric.target)}`);
    lines.push(`- ${labels.decisionThreshold}: ${escapeMarkdown(metric.decisionThreshold)}`);
    lines.push(`- ${labels.pairedGuardrail}: ${escapeMarkdown(metric.pairedGuardrail)}`);
    lines.push(`- [ ] ${labels.checklist.sourceConfirmed} — ${escapeMarkdown(metric.dataSource)}`);
    lines.push(`- [ ] ${labels.checklist.baselineMeasured} — ${escapeMarkdown(metric.baseline)}`);
    lines.push(`- [ ] ${labels.checklist.dashboardSetUp}`);
    lines.push(`- [ ] ${labels.checklist.ownerNamed} — ${escapeMarkdown(metric.owner)}`);
    lines.push(`- [ ] ${labels.checklist.reviewPlanned} — ${escapeMarkdown(metric.cadence)}`);
    lines.push('');
  });

  // Paired metrics summary
  const businessMetrics = plan.metrics.filter((m) => m.category === 'business');
  if (businessMetrics.length > 0) {
    lines.push(`### ${labels.pairedSummaryTitle}`);
    businessMetrics.forEach((m) => {
      lines.push(
        `- ${categoryEmoji(m.category)} **${escapeMarkdown(m.name)}** ↔ ${categoryEmoji('quality')} **${escapeMarkdown(m.pairedGuardrail)}**`
      );
    });
    lines.push('');
  }

  lines.push(`## ${labels.instrumentationPlan}`);
  plan.instrumentationPlan.forEach((item) => {
    lines.push(`- [ ] ${item}`);
  });
  lines.push('');

  lines.push(`## ${labels.acceptanceCriteria}`);
  plan.acceptanceCriteria.forEach((item) => {
    lines.push(`- [ ] ${item}`);
  });
  lines.push('');

  lines.push(`## ${labels.technicalDebt}`);
  plan.technicalDebt.forEach((item) => {
    lines.push(`### ${item.type}`);
    lines.push(`- **Description:** ${item.description}`);
    lines.push(`- **Impact:** ${item.impact}`);
    lines.push(`- **Mitigation:** ${item.mitigation}`);
    lines.push('');
  });

  lines.push(`## ${labels.businessDebt}`);
  plan.businessDebt.forEach((item) => {
    lines.push(`### ${item.type}`);
    lines.push(`- **Description:** ${item.description}`);
    lines.push(`- **Impact:** ${item.impact}`);
    lines.push(`- **Mitigation:** ${item.mitigation}`);
    lines.push('');
  });

  lines.push(`## ${labels.aiEvaluation}`);
  lines.push(
    `- **${isEn(lang) ? 'AI relevant' : 'IA concernée'}:** ${plan.aiEvaluation.isAiRelevant ? labels.yes : labels.no}`
  );
  if (plan.aiEvaluation.isAiRelevant) {
    lines.push(`- **${isEn(lang) ? 'Measures' : 'Mesures'}:**`);
    plan.aiEvaluation.measures.forEach((m) => lines.push(`  - ${m}`));
  }
  lines.push(
    `- **${isEn(lang) ? 'Risk summary' : 'Synthèse des risques'}:** ${plan.aiEvaluation.riskSummary}`
  );
  lines.push('');

  lines.push(`## ${labels.continueCorrectPivotStop}`);
  plan.continueCorrectPivotStopRules.forEach((rule) => {
    lines.push(`- **${isEn(lang) ? 'If' : 'Si'} ${rule.condition}:** ${rule.action}`);
  });
  lines.push('');

  lines.push(`## ${labels.handoverCriteria}`);
  plan.handoverCriteria.forEach((item) => {
    lines.push(`- ${item}`);
  });
  lines.push('');

  lines.push(`## ${labels.first30Days}`);
  labels.first30DaysItems.forEach((item) => {
    lines.push(`- [ ] ${item}`);
  });
  lines.push('');

  lines.push(`## ${labels.missingInformation}`);
  plan.missingInformation.forEach((item) => {
    lines.push(`- ${item}`);
  });
  lines.push('');

  lines.push('---');
  lines.push('');
  lines.push(`> ${labels.automationNote}`);
  lines.push('');

  return lines.join('\n');
}

function isEn(lang: Lang): boolean {
  return lang === 'en';
}

export function downloadPlanMarkdown(plan: MeasurementPlan, lang: Lang, fileName?: string) {
  if (typeof window === 'undefined') {
    return;
  }

  const markdown = buildConfluenceMarkdown(plan, lang);
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName || slugify(plan.title)}.md`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
